import React, { useState } from "react";
import { Calendar } from "react-calendar"; // react-calendar 패키지를 사용한다고 가정
import { Value } from "react-calendar/dist/cjs/shared/types";
import { GET_SELLER_STORE } from "../../api/seller-store";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { formatDateToKoreaTime } from "../../../../utils/date/format-date";
import { gql } from "@apollo/client";
import clsx from "clsx";
import { notify, notifyError } from "../../../../utils/toast";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import {
  PreOrderSchedule,
  useReserveException,
} from "../context/reserve-exception-provider";
import { GET_PRE_ORDER_SCHEDULES } from "./pre-order-weekly-schedule";

const calendarStyles = `
  .react-calendar {
    width: 100% !important;
    max-width: none !important;
    border: none !important;
  }

  .react-calendar__tile {
    text-align: left !important;
    padding: 1.5em 0.5em !important;
  }
  
  .react-calendar__month-view__days__day {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-center !important;
    text-align: center !important;
  }

  .react-calendar__tile--active {
    background: none !important;
    color: inherit !important;
  }

  .react-calendar__tile--now {
    background: none !important;
    color: inherit !important;
  }

  .highlight {
    background-color: green; /* 예약 가능 날짜 색상 */
    color: white; /* 텍스트 색상 */
  }
  .hidden {
    display: none; /* 숨기기 */
  }

  .not-reservable {
    background-color: red; /* 예약 불가능 날짜 색상 */
    color: white; /* 텍스트 색상 */
  }

  /* 주말 색상 변경 */
  .react-calendar__month-view__days__day--weekend {
    color: inherit !important;
  }

  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none;
  }
`;

export const GET_RESERVE_EXCEPTIONS_BY_MONTH = gql`
  query GetReserveExceptions {
    getReserveExceptions {
      id
      dateString
      start
      end
      isReservable
      note
    }
  }
`;

const TOGGLE_RESERVE_EXCEPTIONS = gql`
  mutation toggleReserveExceptions($exceptions: Json!) {
    toggleReserveExceptions(exceptions: $exceptions) {
      ok
      error
    }
  }
`;

interface ReserveException {
  id: string;
  dateString: string;
  start?: string;
  end?: string;
  isReservable: boolean;
  note?: string;
}

interface DateStatus {
  date: string;
  isReservable: boolean;
  dateString: string;
  isSelected?: boolean;
}

const ReserveCalendar = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState<Value>(null);
  const { preOrderSchedules, setPreOrderSchedules } = useReserveException();
  const { dateStatuses, setDateStatuses } = useReserveException();
  const [isEditing, setIsEditing] = useState(false);
  const [initialStatuses, setInitialStatuses] = useState<DateStatus[]>([]);
  const [toggleReserveExceptions] = useMutation(TOGGLE_RESERVE_EXCEPTIONS);
  const [getReserveExceptions] = useLazyQuery(GET_RESERVE_EXCEPTIONS_BY_MONTH, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data) {
        const reserveExceptions = data.getReserveExceptions;
        const updatedStatuses = dateStatuses.map((status) => {
          const exception = reserveExceptions.find(
            (ex: ReserveException) => ex.dateString === status.date
          );
          if (exception) {
            return {
              ...status,
              isReservable: exception.isReservable,
            };
          }
          return status;
        });
        setInitialStatuses(updatedStatuses);
        setDateStatuses(updatedStatuses);
      }
    },
  });
  useQuery(GET_SELLER_STORE, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const currentDate = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1
      );
      const daysInMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      ).getDate();
      const daysInMonth2 = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 2,
        0
      ).getDate();
      const reserveTime = data?.getSellerStore?.reserveTime;
      const initialStatuses: DateStatus[] = [];

      if (reserveTime) {
        for (let i = 0; i < daysInMonth + daysInMonth2; i++) {
          const dateToCheck = new Date();
          dateToCheck.setDate(currentDate.getDate() + i);
          const dayOfWeek = ((dateToCheck.getDay() + 6) % 7).toString();
          const isReservable = reserveTime[dayOfWeek]?.enabled;
          if (isReservable) {
            initialStatuses.push({
              date: formatDateToKoreaTime(dateToCheck),
              isReservable: isReservable || false,
              dateString: formatDateToKoreaTime(dateToCheck),
            });
          }
        }
      }
      setInitialStatuses(initialStatuses);
      setDateStatuses(initialStatuses);
      getReserveExceptions();
    },
  });

  const handleDateChange = (newValue: Value) => {
    const selectedDate = formatDateToKoreaTime(newValue as Date);
    setValue(newValue);
    // 선택된 날짜의 상태를 반전
    setDateStatuses((prev) => {
      const existingStatus = prev.find(
        (status) => status.date === selectedDate
      );
      if (existingStatus) {
        setIsEditing(true);
        // 이미 존재하는 날짜의 상태를 반전
        return prev.map((status) =>
          status.date === selectedDate
            ? {
                ...status,
                isReservable: !status.isReservable,
                dateString: selectedDate,
                isSelected: true,
              }
            : status
        );
      }
      return prev;
    });
  };

  const handleApplyChanges = () => {
    // 상태를 적용하는 로직을 추가하세요.
    setIsEditing(false);

    const selectedDates = dateStatuses.filter((status) => status.isSelected);
    toggleReserveExceptions({
      variables: {
        exceptions: selectedDates.map((status) => ({
          dateString: status.date,
          isReservable: status.isReservable,
        })),
      },
      onCompleted: (data) => {
        if (data.toggleReserveExceptions.ok) {
          notify("변경되었습니다");
          setDateStatuses((prev) =>
            prev.map((status) => ({
              ...status,
              isSelected: false, // isSelected를 false로 설정하여 풀어줌
            }))
          );

          // ---------------- 타 컴포넌트의 상태 업데이트 ----------------
          // 위클리 요약 업데이트 // pre-order-weekly-schedule.tsx 참고
          const updatedPreOrderSchedules = preOrderSchedules.map((schedule) => {
            const status = selectedDates.find(
              (status) => status.date === schedule.dateString
            );
            return status
              ? { ...schedule, isReservable: status.isReservable }
              : schedule;
          });
          setPreOrderSchedules(updatedPreOrderSchedules);
          // ---------------- 타 컴포넌트의 상태 업데이트 ----------------
        }
      },
      onError: (error) => {
        notifyError("에러가 발생했습니다. 지속될 경우 고객센터 문의 바랍니다.");
        Sentry.captureException(error);
      },
      update: (cache, { data }) => {
        if (data) {
          cache.modify({
            id: "ReserveExceptions",
            fields: {
              getReserveExceptions(existingExceptions = []) {
                const newExceptions = data.toggleReserveExceptions.exceptions;
                return [...existingExceptions, ...newExceptions];
              },
            },
          });
        }

        // ---------------- 타 컴포넌트의 캐시 업데이트 ----------------
        // 위클리 요약 업데이트 // pre-order-weekly-schedule.tsx 참고
        const existingData = cache.readQuery({
          query: GET_PRE_ORDER_SCHEDULES,
        }) as { preOrderSchedules: PreOrderSchedule[] };

        if (existingData) {
          const updatedPreOrderSchedules = existingData.preOrderSchedules.map(
            (schedule) => {
              const status = selectedDates.find(
                (status) => status.date === schedule.dateString
              );
              return status
                ? { ...schedule, isReservable: status.isReservable }
                : schedule;
            }
          );

          cache.writeQuery({
            query: GET_PRE_ORDER_SCHEDULES,
            data: { preOrderSchedules: updatedPreOrderSchedules },
          });
        }
        // ---------------- 타 컴포넌트의 캐시 업데이트 ----------------
      },
    });
  };

  const handleResetChanges = () => {
    setDateStatuses(initialStatuses); // 상태 초기화
    setIsEditing(false);
  };

  const today = new Date();
  const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0); // 다음달 마지막 날
  return (
    <div className="my-4">
      <hr className="my-4" />
      <style>{calendarStyles}</style>
      <div className="flex items-end justify-between mb-4">
        <div className="flex flex-col">
          <h2 className="text-2xl font-bold inline">🗓️ 클릭하여 닫기/열기!</h2>
          <span className="text-gray-500 text-lg ml-7">
            날짜를 클릭해보세요!
          </span>
        </div>
        <span
          className="text-black text-lg p-2 rounded-md bg-white border-2 border-gray-400"
          onClick={() => {
            navigate("/boss/pre-order/pickup-time-update");
          }}
        >
          오픈 요일 변경
        </span>
      </div>
      <div className="flex justify-center mb-2">
        <div className="flex items-center mr-4">
          <div className="bg-blue-400 rounded-full w-4 h-4 mr-2"></div>
          <span>열어놓은 날</span>
        </div>
        <div className="flex items-center">
          <div className="bg-orange-400 rounded-full w-4 h-4 mr-2"></div>
          <span>닫아놓은 날</span>
        </div>
      </div>
      <Calendar
        className="rounded-xl shadow-lg border"
        calendarType="gregory"
        onChange={handleDateChange}
        value={value}
        minDate={startOfThisMonth}
        maxDate={endOfNextMonth}
        tileContent={({ date }) => {
          const dateStatus = dateStatuses.find(
            (status) => status.date === formatDateToKoreaTime(date)
          );
          if (dateStatus) {
            return (
              <div className="flex justify-center items-center w-full h-full">
                <div
                  className={clsx(
                    dateStatus.isReservable ? "bg-blue-400" : "bg-orange-400",
                    "rounded-full w-4 h-4 flex justify-center items-center transition-transform duration-300 transform",
                    dateStatus.isSelected
                      ? "border-dashed border-2 border-black scale-150" // 선택된 상태에서 크기 증가
                      : ""
                  )}
                ></div>
              </div>
            );
          }
          return null; // 예약 예외가 없는 날짜는 null 반환
        }}
      />

      <div
        className={`flex justify-between text-2xl mt-4 gap-4 ${
          isEditing ? "opacity-100" : "opacity-50"
        }`}
      >
        <button
          className="bg-gray-500 text-white px-4 py-2 rounded-md"
          onClick={handleResetChanges}
          disabled={!isEditing}
        >
          초기화
        </button>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded-md"
          onClick={handleApplyChanges}
          disabled={!isEditing}
        >
          적용하기
        </button>
      </div>
    </div>
  );
};

export default ReserveCalendar;
